import { COLORS, TAB_TYPE, Tab } from '@repo/design-system/index'
import { QIcon_beta, QIcon_cart, QIcon_menu_button_hamburger } from '@repo/icons/index'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { useAITutorPermission, useAITutorStreak, useCourseDetail } from '../../../modules/apis/student/ai-tutor'
import { useConversationEditUserSetting } from '../../../modules/apis/student/conversation'
import { useCustomerType } from '../../../modules/apis/student/products'
import { useLocale } from '../../../modules/context/LocaleContext'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME, STACK_ACTIVITY_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter, { sendPostMessageOnlyMessage } from '../../../modules/hooks/common/useAppRouter'
import { useIsOkToUse } from '../../../modules/hooks/useAppVersionStatus'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_ai_tutor, n_aitutor } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import urls from '../../../modules/urls'
import { AI_TUTOR_COURSE } from '../../../modules/vars'
import { useAITutorStack } from '../../../pages/[lang]/webview/ai-tutor'
import LoadingSpinner from '../../common/LoadingSpinner'
import { ReactComponent as FireActive } from '../../common/icons/fire_active.svg'
import { ReactComponent as FireInactive } from '../../common/icons/fire_inactive.svg'
import AITutorTutorial from '../../new-portal/ai-tutor/tutorial'
import WebviewStackContainer from '../common/WebviewStackContainer'

function Loading() {
  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <LoadingSpinner lg show primary />
    </div>
  )
}

const AITutorMain = dynamic(() => import('../../new-portal/ai-tutor/AITutor'), { loading: Loading })
const AITutorAchievement = dynamic(() => import('../../new-portal/ai-tutor/AITutorAchievement'), { loading: Loading })
const AITutorRoleplayList = dynamic(() => import('../../new-portal/ai-tutor/AITutorRoleplayList'), { loading: Loading })
const AITutorDiscussion = dynamic(() => import('../../new-portal/ai-tutor/AITutorDiscussion'), { loading: Loading })
const AITutorCall = dynamic(() => import('../../new-portal/ai-tutor/AITutorCall'), { loading: Loading })
const CallPreference = dynamic(() => import('../../new-portal/ai-tutor/CallPreference'), { loading: Loading })

function AiTutorHeader({ activeTabIdx, setActiveTabIdx, setHideTutorial }) {
  const { useAITutorFlow } = useAITutorStack()
  const { push } = useAITutorFlow()
  const getFullUrl = useReturnFullUrl()
  const appRouter = useAppRouter({ stackPush: push, browserDetect: true })
  const { t } = useTranslation()
  const { data: streakData } = useAITutorStreak()
  const today = streakData?.streak?.is_today_streak
  const { data: customerType } = useCustomerType()
  const { data: aiTutorMemebershipPermissionData, refetch } = useAITutorPermission(AI_TUTOR_COURSE)
  const { mutate: edit } = useConversationEditUserSetting()
  const isMultiCampusUser = customerType?.isMulticampus
  const { currentUser } = useCurrentUser()
  const isOkToUse = useIsOkToUse()
  const isAITrialFree = currentUser?.ai_tutor_permission?.source_type === 'FreeAITutorMembershipGrantRecord'

  useEffect(() => {
    refetch()
  }, [])

  const onClickStreak = () => {
    appRouter.push(null, {
      stackActivity: STACK_ACTIVITY_NAME.AI_TUTOR_STREAK_ACTIVITY,
    })
  }
  const onClickPurchaseList = () => {
    appRouter.push(urls.webview.aiTutor.purchase, {
      appScreenName: APP_SCREEN_NAME.IAP_SCREEN,
      appSendData: { url: getFullUrl(urls.webview.aiTutor.purchase) },
    })
  }
  const handleClickMenu = () => {
    sendPostMessageOnlyMessage(JSON.stringify({ type: 'CLICK_MENU' }))
  }

  const handleShowTutorial = () => {
    if (window.confirm('링글팀에게만 보이는 튜토리얼 다시보기입니다.')) {
      edit(
        { key: 'hide_ai_tutor_tutorial', value: false },
        {
          onSuccess: () => {
            setHideTutorial(false)
          },
        }
      )
    }
  }

  return (
    <div className="flex w-screen flex-col">
      <div className="flex flex-row items-center justify-between px-[20px] h-[44px]">
        <div className="flex h-full flex-row items-center justify-center gap-x-[20px] Title1-20Bd">
          {t(n_aitutor.title)}
        </div>
        <div className="flex h-full flex-row items-center justify-center gap-x-[20px]">
          {/* {currentUser?.is_ringle_member && (
            <div onClick={handleShowTutorial} className="Caption-12Md">
              <QIcon_exclamation_filled2 width={24} height={24} />
            </div>
          )} */}

          {today ? (
            <FireActive width={24} height={24} onClick={onClickStreak} />
          ) : (
            <FireInactive width={24} height={24} onClick={onClickStreak} />
          )}
          {customerType?.isDirectPurchaser &&
            aiTutorMemebershipPermissionData?.current === null &&
            aiTutorMemebershipPermissionData?.temp_membership === null && (
              <QIcon_cart onClick={onClickPurchaseList} width={24} height={24} />
            )}
          <QIcon_menu_button_hamburger onClick={handleClickMenu} width={24} height={24} />
        </div>
      </div>
      <div className="h-[52px] w-full border-b-1 border-gray-100">
        <Tab
          className="h-[46px]"
          type={TAB_TYPE.SCROLL}
          borderNone={true}
          activeTabIdx={activeTabIdx}
          setActiveTabIdx={setActiveTabIdx}
        >
          <Tab.TabItem name={t(n_ai_tutor.menu.subtitle1)} />
          <Tab.TabItem name={t(n_ai_tutor.menu.subtitle2)} />
          <Tab.TabItem name={t(n_ai_tutor.menu.subtitle3)} />

          {!isMultiCampusUser && isOkToUse && (
            <Tab.TabItem
              name={t(n_ai_tutor.menu.subtitle5)}
              icon={
                <QIcon_beta width={21} height={16} color={activeTabIdx === 3 ? COLORS.PURPLE400 : COLORS.GRAY400} />
              }
            />
          )}

          <Tab.TabItem name={t(n_ai_tutor.menu.subtitle4)} />
        </Tab>
      </div>
    </div>
  )
}

export default function AiTutorActivity() {
  const router = useAppRouter()
  const courseId = +router.query?.courseId || 'latest'
  const tabId = router.query?.tabId
  const [activeTabIdx, setActiveTabIdx] = useState(+tabId || 0)
  const [hideTutorial, setHideTutorial] = useState(true)
  const { data: courseData } = useCourseDetail(courseId)
  const { locale } = useLocale()
  const { data: customerType } = useCustomerType()
  const isMultiCampusUser = customerType?.isMulticampus
  const isOkToUse = useIsOkToUse()

  useEffect(() => {
    if (+tabId) {
      setActiveTabIdx(+tabId || 0)
    }
  }, [tabId])

  // TODO: 튜토리얼 임시 비활성화, 추후 개선 예정
  // https://ringle.atlassian.net/browse/AITUTOR-156
  // useEffect(() => {
  //   axiosGet(apis.conversation.users.getSetting('hide_ai_tutor_tutorial')).then((res) => {
  //     setHideTutorial(!!res.value)
  //   })
  // }, [])
  // return <></>

  const aiTutorContentComponents = [
    () => <AITutorMain key={0} courseData={courseData || {}} />,
    () => <AITutorRoleplayList key={1} />,
    () => <AITutorDiscussion key={2} />,
    isMultiCampusUser || !isOkToUse ? null : () => <AITutorCall key={3} />,
    () => <AITutorAchievement key={4} setActiveTabIdx={setActiveTabIdx} />,
  ]

  const activeComponents = aiTutorContentComponents.filter(Boolean)

  return (
    <div style={{}}>
      <AITutorTutorial
        hideTutorial={hideTutorial}
        setHideTutorial={setHideTutorial}
        locale={locale == 'ko' ? 'ko' : 'en'}
      />
      <WebviewStackContainer
        height="96px"
        title={
          <AiTutorHeader
            activeTabIdx={activeTabIdx}
            setActiveTabIdx={setActiveTabIdx}
            setHideTutorial={setHideTutorial}
          />
        }
        border={false}
      >
        {activeComponents[activeTabIdx || 0]?.()}
      </WebviewStackContainer>
    </div>
  )
}
